// Open-order card — the card-view counterpart of PositionTracker's Open
// Orders table row. Layout follows the client trader's mockup: status +
// side + venue badges, book-position badge, market header, qty/bid/last/
// queue stats, and a cost / to-win / resting-age footer.

import { Zap } from 'lucide-react';
import { VenueChip } from '../atoms/VenueChip';
import { OrderActionButtons } from '../molecules';
import {
  CardShell,
  CardHeader,
  CardBody,
  CardFooter,
  StatLine,
  Pill,
  SideBadge,
  BetTeamTitle,
} from './cardKit';
import { fmtCents, fmtQty, fmtUsd, fmtAge, fmtCompactSigned } from './cardFormat';
import {
  queueBehindTone,
  queueCardTone,
  queueTone,
  queueValuePulse,
  type OrderCardVM,
} from './orderCardModel';
import { orderVelocity, type VelocityWindow } from './orderVelocity';
import { priceVelocity, sideNativePriceDelta } from './priceVelocity';
import { DivergenceBadge } from './DivergenceBadge';
import type { DivergenceInfo } from '@/lib/divergence/polyKalshiDivergence';

function PriceWithOdds({
  cents,
  odds,
}: {
  cents: number | null;
  odds: string | null;
}) {
  const tone =
    cents == null
      ? 'text-muted-foreground'
      : cents > 50
        ? 'text-red-300'
        : cents < 50
          ? 'text-green-300'
          : 'text-muted-foreground';
  return (
    <span className={tone}>
      {fmtCents(cents)}
      {odds && (
        <span className="ml-1.5 text-[11px]">{odds}</span>
      )}
    </span>
  );
}

function velToneClass(d: number, filling: boolean): string {
  if (filling) return d > 0 ? 'text-amber-300' : 'text-muted-foreground';
  // Resting: a shrinking queue (negative) means the order is advancing.
  if (d < 0) return 'text-green-400';
  if (d > 0) return 'text-red-400';
  return 'text-muted-foreground';
}

/** Footer velocity line — queue movement for resting orders, fill progress
 *  for filling ones. Renders nothing until history covers at least one
 *  window (fresh page loads have no history yet). */
function VelocityLine({ vm }: { vm: OrderCardVM }) {
  if (vm.venue !== 'kalshi' || (!vm.filling && vm.queue == null)) return null;
  const v = orderVelocity(vm.key);
  const windows: { label: string; w: VelocityWindow }[] = [
    { label: '1m', w: v.m1 },
    { label: '5m', w: v.m5 },
    { label: '30m', w: v.m30 },
    { label: '1h', w: v.h1 },
  ];
  const cells = windows
    .map(({ label, w }) => {
      const d = vm.filling ? w.filledDelta : w.queueDelta;
      return d == null ? null : { label, d };
    })
    .filter((c): c is { label: string; d: number } => c !== null);
  if (cells.length === 0) {
    return (
      <div
        className="text-muted-foreground mt-1.5 text-[11px]"
        title="Velocity appears after one minute of visible order history"
      >
        velocity warming
      </div>
    );
  }
  return (
    <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-2 text-[11px]">
      {cells.map(({ label, d }) => (
        <span key={label}>
          {label}{' '}
          <span className={`font-mono ${velToneClass(d, vm.filling)}`}>
            {vm.filling ? fmtQty(d) : fmtCompactSigned(d)}
          </span>
        </span>
      ))}
      {vm.filling && <span>filled</span>}
    </div>
  );
}

export function OrderCard({
  vm,
  onCancel,
  onEdit,
  onOpen,
  canceling,
  queueCriticalFlash = false,
  divergence,
  divergenceFlash = false,
}: {
  vm: OrderCardVM;
  onCancel: () => void | Promise<void>;
  /** Open the EditOrderPriceModal seeded with this order's params. Parent
   *  owns the modal state — this prop just signals intent. */
  onEdit: () => void;
  onOpen?: () => void;
  canceling: boolean;
  /** Brief one-shot cue when the order crosses into the sub-10k queue band. */
  queueCriticalFlash?: boolean;
  /** Cross-venue divergence for this market (Poly vs Kalshi), or null. The
   *  badge self-gates on threshold + poly freshness. */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
}) {
  const qTone = queueTone(vm.queue, vm.queueTotal);
  const qCardTone = queueCardTone(vm.queue, vm.queueTotal);
  const qPulse = queueValuePulse(vm.queue);

  const statusPill = vm.activeInSale ? (
    <Pill
      className="bg-amber-400/20 text-amber-300"
      title="Order is partially filled and has no better level ahead"
    >
      <Zap size={9} /> In sale
    </Pill>
  ) : (
    <Pill
      className="bg-blue-500/20 text-blue-300"
      title="Order is resting in the book"
    >
      Resting
    </Pill>
  );

  const bookPill = (() => {
    if (vm.bookDiff == null) return null;
    if (vm.bookDiff === 0) {
      return (
        <Pill
          className="bg-green-500/20 text-green-400"
          title="At the best price level; queue still shows FIFO depth at this price"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
          At front
        </Pill>
      );
    }
    if (vm.bookDiff === 1) {
      return (
        <Pill className="bg-yellow-500/20 text-yellow-300">
          <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
          Behind 1¢
        </Pill>
      );
    }
    return (
      <Pill className="bg-red-500/20 text-red-300">
        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
        Behind {vm.bookDiff}¢
      </Pill>
    );
  })();

  return (
    <CardShell
      standout={vm.activeInSale}
      accentClass={qCardTone}
      className={[
        queueCriticalFlash && 'animate-queue-critical-card-flash',
        divergenceFlash && 'animate-divergence-flash',
      ]
        .filter(Boolean)
        .join(' ')}
      title={vm.ticker}
      ariaLabel={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
    >
      <CardHeader
        leftBadges={
          <>
            {statusPill}
            {vm.side && <SideBadge side={vm.side} team={vm.team} />}
            <VenueChip venue={vm.venue} />
          </>
        }
        rightBadges={
          <>
            {bookPill}
            <DivergenceBadge info={divergence} />
          </>
        }
        title={<BetTeamTitle title={vm.title} team={vm.team} />}
        subtitle={vm.subtitle}
      />

      <CardBody>
        <StatLine
          label="qty"
          value={
            vm.filling
              ? `${fmtQty(vm.filledQty)} / ${fmtQty(vm.totalQty)}`
              : fmtQty(vm.totalQty)
          }
        />
        <StatLine
          label={vm.isSell ? 'ask' : 'bid'}
          value={<PriceWithOdds cents={vm.bidCents} odds={vm.bidOdds} />}
        />
        <StatLine label="start" value={vm.eventStartLabel || '—'} />
        <StatLine
          label="last"
          value={<PriceWithOdds cents={vm.lastCents} odds={vm.lastOdds} />}
        />
        {/* 1m price movement — side-native (NO orders flip the sign so a
            YES 50→52 reads as `-2¢` for the NO holder). Polymarket cards
            skip the chip because marketStore doesn't surface poly current
            price yet (no source to sample). Hidden until 1m of history
            has accumulated for the ticker. */}
        {vm.venue === 'kalshi' && vm.side != null && (() => {
          const yesM1 = priceVelocity(vm.ticker).m1.priceDelta;
          const delta = sideNativePriceDelta(yesM1, vm.side);
          if (delta == null) return null;
          const tone =
            delta > 0
              ? 'text-green-400'
              : delta < 0
                ? 'text-red-400'
                : 'text-muted-foreground';
          return (
            <StatLine
              label="1m Δ"
              value={`${delta >= 0 ? '+' : ''}${delta}¢`}
              valueClass={tone}
            />
          );
        })()}
        <StatLine
          label="queue"
          value={
            vm.queue == null ? '—' : vm.queue < 1 ? 'Front' : fmtQty(vm.queue)
          }
          valueClass={`${qTone} ${qPulse}`}
        />
        <StatLine
          label="level above"
          value={vm.queueAbove == null ? '—' : fmtQty(vm.queueAbove)}
          valueClass={queueTone(vm.queueAbove, vm.queueTotal)}
        />
        <StatLine
          label="in line behind"
          value={vm.queueBehind == null ? '—' : fmtQty(vm.queueBehind)}
          valueClass={queueBehindTone(vm.queueBehind)}
        />
      </CardBody>

      <CardFooter>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground text-xs">
            cost{' '}
            <span className="text-foreground font-mono font-semibold">
              {fmtUsd(vm.costDollars)}
            </span>
          </span>
          <span className="text-muted-foreground text-xs">
            to win{' '}
            <span className="text-positive font-mono font-semibold">
              {fmtUsd(vm.toWinDollars)}
            </span>
          </span>
        </div>
        <VelocityLine vm={vm} />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-muted-foreground text-[11px]">
            {vm.tifLabel !== 'GTC' && (
              <span className="mr-2">exp {vm.tifLabel}</span>
            )}
            {vm.restingSinceMs != null && `resting ${fmtAge(vm.restingSinceMs)}`}
          </span>
          <OrderActionButtons
            canceling={canceling}
            onCancel={onCancel}
            onEdit={onEdit}
          />
        </div>
      </CardFooter>
    </CardShell>
  );
}
